import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CARGO_LABELS } from "@/lib/constants";
import { InsuranceCalculator } from "@/components/InsuranceCalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatEUR } from "@/lib/constants";

export function BookingDialog({ route, user, trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cargoType, setCargoType] = useState(route.cargo_types?.[0] || "");
  const [desc, setDesc] = useState("");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [weight, setWeight] = useState("");
  const [price, setPrice] = useState("");
  const [insEnabled, setInsEnabled] = useState(false);
  const [cargoValue, setCargoValue] = useState("");

  const stopsOptions = [route.origin, ...(route.stops || []), route.destination];

  const submit = async () => {
    if (!cargoType) { toast.error("Seleziona il tipo di merce"); return; }
    setLoading(true);
    try {
      await api.post("/bookings", {
        route_id: route.route_id,
        cargo_type: cargoType,
        cargo_description: desc,
        pickup_stop: route.partial_available ? pickup : null,
        dropoff_stop: route.partial_available ? dropoff : null,
        weight_kg: weight ? Number(weight) : null,
        offered_price: price ? Number(price) : null,
        insurance_enabled: insEnabled,
        cargo_value: insEnabled ? Number(cargoValue || 0) : 0,
      });
      toast.success("Richiesta inviata al trasportatore!");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Richiedi trasporto</DialogTitle>
          <DialogDescription>{route.origin} → {route.destination}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Tipo di merce</Label>
            <Select value={cargoType} onValueChange={setCargoType}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="booking-cargo-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>
                {route.cargo_types?.map((c) => <SelectItem key={c} value={c}>{CARGO_LABELS[c] || c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {route.partial_available && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ritiro presso</Label>
                <Select value={pickup} onValueChange={setPickup}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="booking-pickup-select"><SelectValue placeholder="Origine" /></SelectTrigger>
                  <SelectContent>{stopsOptions.map((s, i) => <SelectItem key={i} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Consegna presso</Label>
                <Select value={dropoff} onValueChange={setDropoff}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="booking-dropoff-select"><SelectValue placeholder="Destinazione" /></SelectTrigger>
                  <SelectContent>{stopsOptions.map((s, i) => <SelectItem key={i} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Peso (kg)</Label>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-sm mt-1" data-testid="booking-weight-input" />
            </div>
            <div>
              <Label className="text-xs">Offerta (€)</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={`${formatEUR(route.price_min)}`} className="rounded-sm mt-1" data-testid="booking-price-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Descrizione merce</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="booking-desc-input" />
          </div>

          <InsuranceCalculator enabled={insEnabled} setEnabled={setInsEnabled} value={cargoValue} setValue={setCargoValue} />
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full" data-testid="booking-submit-btn">
            {loading ? "Invio…" : "Invia richiesta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
